'use client'

import { BalconyIcon } from '@/components/foundations/BalconyIcon'
import { CropLandscapeIcon } from '@/components/foundations/CropLandscapeIcon'
import { CropPortraitIcon } from '@/components/foundations/CropPortraitIcon'
import { WavesProductIcon } from '@/components/foundations/WavesProductIcon'
import useCurrentMarket from '@/features/caching/hooks/useCurrentMarket'
import { FlyerTemplate, FlyerType, Orientation, Templates, TemplateStructure } from '@/features/flyer/model/templates'
import { currencySymbolMap } from '@/features/markets/configs/currencies'
import { TranslationBundles } from '@/features/sitecore/configs/translation-bundles'
import { useSitecoreState } from '@/features/sitecore/store/useSitecoreState'
import { useGoogleAnalytics } from '@/utils/hooks/useGoogleAnalitics'
import { useModalState } from '@/utils/hooks/useModalState'
import { useRouter } from 'next/navigation'
import { ReactElement, useState } from 'react'
import { ActionLink, Button, InfoIcon, SwiperWrapperBYF, TabContent, Tabs } from '../uiComponents'

interface FlyerTemplateProps {
	flyerTemplates: Templates
}

export const FlyerBuilderSelectTypeSection = (props: FlyerTemplateProps) => {
	const modalState = useModalState()
	const router = useRouter()
	const market = useCurrentMarket()
	const { pushEvent } = useGoogleAnalytics()

	const [orientationPreview, setOrientationPreview] = useState<Orientation>('portrait')
	const [selectedTemplate, setSelectedTemplate] = useState<FlyerTemplate | undefined>(undefined)
	const [selectedTabIndex, setSelectedIndex] = useState(0)
	const [isRedirecting, setIsRedirecting] = useState(false)

	const selectedFlyerType = selectedTabIndex === 0 ? 'tactical' : 'product'

	const sitecoreStore = useSitecoreState((state) => state)
	const readInstructionsLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-READ-INSTRUCTIONS', 'Read instructions')
	const nextLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-NEXT', 'Next')
	const tacticalLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-TACTICAL-LABEL', 'Tactical')
	const productLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-PRODUCT-LABEL', 'Product')
	const byfInstructionLabel = sitecoreStore.getLabel(TranslationBundles.BUILD_YOUR_FLYER, 'MSC-BUILD_YOUR_FLYER-INSTRUCTIONS-FIST-STEP', 'This flyer builder is designed to convey specific commercial information about MSC offers. It allows you to select the which type of flyer you would use. You can select also the orientation of your flyer. Then you can start to build your Build your Flyer.')

	const handleImageClick = (type: FlyerType, orientation: Orientation, identifier: string) => {
		const selected = templates[type][orientation].find((template) => template.identifier === identifier)
		setSelectedTemplate(selected)
	}

	const handleOrientationChange = (orientation: Orientation) => {
		setOrientationPreview(orientation)
	}

	const handleNextClick = async () => {
		if (!selectedTemplate) return
		setIsRedirecting(true)
		selectFlyerTypeGA4Event(selectedTemplate)
		router.push(`/${market.locale}/flyer-builder/${selectedTemplate.flyerType}/${selectedTemplate.identifier}/${selectedTemplate.orientation}`)
	}

	const handleClickTab = (tabIndex: number) => {
		if (selectedTabIndex !== tabIndex) {
			setSelectedTemplate(undefined)
		}
		setSelectedIndex(tabIndex)
	}

	const updateTemplates = (flyerTemplates: FlyerTemplate[]) => {
		flyerTemplates.forEach((flyerTemplate) => {
			const templateHtml = new DOMParser().parseFromString(flyerTemplate.template, 'text/html')
			const currencySymbol = currencySymbolMap[market.currency]
			//replace currency template
			const currencyElements = templateHtml.querySelectorAll('.currency')
			//remove agency section info on the template
			const agencySection = templateHtml.querySelectorAll('#last-paragraph')
			currencyElements.forEach((element) => (element.innerHTML = currencySymbol))
			agencySection.forEach((element) => element.remove())

			flyerTemplate.template = templateHtml.documentElement.innerHTML
		})

		return flyerTemplates
	}

	type TemplateUI = {
		buttonIcon: ReactElement
		buttonLabel: string
	}

	type TemplatesExtUI = {
		tactical: TemplateStructure & TemplateUI
		product: TemplateStructure & TemplateUI
	}

	const templates: TemplatesExtUI = {
		tactical: {
			portrait: props.flyerTemplates.tactical.portrait,
			landscape: props.flyerTemplates.tactical.landscape,
			buttonIcon: <BalconyIcon />,
			buttonLabel: tacticalLabel,
		},
		product: {
			portrait: props.flyerTemplates.product.portrait,
			landscape: props.flyerTemplates.product.landscape,
			buttonIcon: <WavesProductIcon />,
			buttonLabel: productLabel,
		},
	}

	const selectFlyerTypeGA4Event = (selected: FlyerTemplate) => {
		const byf_template = selected.identifier
		const bundleTemplate = selectedFlyerType === 'tactical' ? TranslationBundles.BUILD_YOUR_FLYER_TACTICAL : TranslationBundles.BUILD_YOUR_FLYER_PRODUCT
		const eventName = `next_byf_${selectedFlyerType}_st1`
		const section_byf = selectedFlyerType.toUpperCase()
		const label_clicked = sitecoreStore.getLabel(bundleTemplate, `MSC-BUILD_YOUR_FLYER-${selectedFlyerType.toUpperCase()}-FLYER-DESCRIPTION-${byf_template}`, 'template')

		pushEvent({ event: 'nextBYF', eventName, label_clicked, section_byf, byf_template })
	}

	return (
		<>
			<div className="flex flex-col w-full h-full">
				<Tabs
					variant="full"
					activeIndex={selectedTabIndex}
					onActiveIndexChanged={handleClickTab}
					tabListClassName="pl-6"
					tabListNearElement={
						<div className={'flex justify-end gap-2 mb-5 pr-6'}>
							<ActionLink
								icon={<InfoIcon size="12" color="#035FBF" />}
								onClick={() => {
									modalState.openBuildYourFlyerInstructionTemplate({
										instructionLabel: byfInstructionLabel,
									})
								}}
							>
								{readInstructionsLabel}
							</ActionLink>
							<button className={`${orientationPreview === 'portrait' ? 'bg-grey' : ''} w-[56px] h-[32px] flex flex-col flex-wrap justify-center items-center`} onClick={() => handleOrientationChange('portrait')}>
								<CropPortraitIcon />
							</button>
							<button className={`${orientationPreview === 'landscape' ? 'bg-grey' : ''} w-[56px] h-[32px] flex flex-col flex-wrap justify-center items-center`} onClick={() => handleOrientationChange('landscape')}>
								<CropLandscapeIcon />
							</button>
						</div>
					}
				>
					{(Object.keys(templates) as Array<keyof TemplatesExtUI>).map((type, index) => (
						<TabContent
							key={`${type}-${index}`}
							button={
								<>
									{templates[type].buttonIcon} {templates[type].buttonLabel}
								</>
							}
						>
							<div className="flex flex-col bg-background gap-6 p-6 bg-grey h-full overflow-hidden">
								<div className="h-full py-12">
									<SwiperWrapperBYF templates={updateTemplates(templates[type][orientationPreview])} selectedImage={selectedTemplate?.identifier} onImageSelected={(identifier) => handleImageClick(type, orientationPreview, identifier)} orientation={orientationPreview} />
								</div>
								<div className="flex justify-end gap-1.5 ml-auto py-4 px-3">
									<Button variant="filled" disabled={!selectedTemplate} onClick={handleNextClick} isSubmitting={isRedirecting}>
										{nextLabel}
									</Button>
								</div>
							</div>
						</TabContent>
					))}
				</Tabs>
			</div>
		</>
	)
}
